import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, UsersThree, Spinner } from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "marketing", label: "Marketing", color: "bg-pink-100 text-pink-700" },
  { value: "sales", label: "Ventas", color: "bg-emerald-100 text-emerald-700" },
  { value: "inventory", label: "Inventario", color: "bg-amber-100 text-amber-700" },
  { value: "super_admin", label: "Admin", color: "bg-zinc-100 text-zinc-700" },
];

const PRIORITY_OPTIONS = [
  { value: "alta", label: "Alta", color: "bg-rose-100 text-rose-700" },
  { value: "media", label: "Media", color: "bg-amber-100 text-amber-700" },
  { value: "baja", label: "Baja", color: "bg-zinc-100 text-zinc-700" },
];

/**
 * Modal para aprobar un item de análisis y enviarlo como tarea al equipo.
 * Props:
 *   - source: "inventory" | "marketing" | "purchasing"
 *   - analysisId: string
 *   - itemIndex: number
 *   - item: el objeto del item (action/recommendation/buy_more)
 *   - defaultRole: string (preseleccionado)
 *   - defaultType: string (ej. "reel", "promo", "comprar_mas")
 *   - onClose, onCreated
 */
export default function TaskAssignModal({ source, analysisId, itemIndex, item, defaultRole, defaultType, onClose, onCreated }) {
  const initialTitle = item?.product_name ? `${defaultType?.toUpperCase()} · ${item.product_name}` : (item?.action || item?.content_type || "Nueva tarea");
  const initialDescription = item?.reason || item?.description || "";
  const [role, setRole] = useState(defaultRole || "marketing");
  const [priority, setPriority] = useState(item?.priority || "media");
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Pon un título"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/alendra/tasks", {
        source,
        source_analysis_id: analysisId,
        source_item_index: itemIndex,
        type: defaultType,
        title: title.trim(),
        description: description.trim(),
        hook: item?.hook || "",
        product_id: item?.product_id || "",
        product_name: item?.product_name || "",
        assigned_role: role,
        priority,
      });
      toast.success(`Tarea asignada al equipo de ${ROLE_OPTIONS.find(r => r.value === role)?.label || role}`);
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la tarea");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="task-assign-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
          <UsersThree size={20} weight="fill" className="text-[#002FA7]"/>
          <h3 className="font-bold text-sm">Asignar tarea al equipo</h3>
          <button type="button" onClick={onClose} className="ml-auto p-1 hover:bg-zinc-100 rounded" data-testid="task-modal-close">
            <X size={16}/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Asignar a equipo</label>
            <div className="flex gap-2 mt-1 flex-wrap" data-testid="task-role-picker">
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  data-testid={`task-role-${r.value}`}
                  className={`px-3 py-1.5 rounded text-xs font-semibold transition border ${role === r.value ? `${r.color} border-current` : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Prioridad</label>
            <div className="flex gap-2 mt-1">
              {PRIORITY_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPriority(p.value)}
                  data-testid={`task-priority-${p.value}`}
                  className={`px-3 py-1.5 rounded text-xs font-semibold transition border ${priority === p.value ? `${p.color} border-current` : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Título</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="task-title-input"
              className="w-full mt-1 border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Descripción / Instrucciones</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              data-testid="task-description-input"
              className="w-full mt-1 border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              placeholder="Detalles que el equipo necesita para ejecutarla"
            />
          </div>
          {item?.hook && (
            <div className="bg-zinc-50 border border-zinc-200 rounded p-2 text-xs">
              <span className="font-semibold text-zinc-600">Hook sugerido:</span>{" "}
              <span className="italic">&ldquo;{item.hook}&rdquo;</span>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded">Cancelar</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="task-submit"
            className="px-4 py-2 text-sm font-semibold bg-[#002FA7] hover:bg-[#002277] text-white rounded inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Spinner className="animate-spin" size={14}/>}
            Aprobar y enviar
          </button>
        </div>
      </div>
    </div>
  );
}

export { ROLE_OPTIONS as TASK_ROLES };
